/**
 * Registre des champs BT suivis par la matrice de couverture.
 *
 * Chaque entrée décrit : le code BT, sa section d'affichage, le profil minimal
 * qui l'exige (`minProfile`, rang 0 = MINIMUM … 3 = EN16931), son caractère
 * obligatoire ou facultatif, et la façon de lire la valeur dans le modèle.
 *
 * La matrice répond à une question simple : « sur les factures du corpus qui
 * DEVRAIENT contenir ce champ, dans combien de cas l'extracteur le trouve-t-il ? »
 */
import type { InvoiceDocument, InvoiceLine, Syntax } from './model';
import { PROFILE_RANK } from './model';
import type { SectionId } from './labels.fr';

export interface FieldDef {
  bt: string;
  section: SectionId;
  /** Rang minimal du profil qui exige le champ. */
  minProfile: 0 | 1 | 2 | 3 | 4;
  /** Champ 0..1 : la présence est un bonus, pas une exigence. */
  optional: boolean;
  /** Restriction de syntaxe (champ spécifique CII par exemple). */
  syntaxes?: Syntax[];
  /** Valeur normalisée, undefined si absente. */
  get(doc: InvoiceDocument): string | undefined;
}

const nonEmpty = (v: string | undefined | null): string | undefined =>
  v == null || String(v).trim() === '' ? undefined : String(v);

/** Un champ de ligne est « présent » s'il l'est sur TOUTES les lignes. */
const allLines = (doc: InvoiceDocument, pick: (line: InvoiceLine) => unknown): string | undefined => {
  if (doc.lines.length === 0) return undefined;
  const values = doc.lines.map(pick);
  if (values.some((v) => v == null || v === '')) return undefined;
  return `×${values.length}`;
};

const someLine = (doc: InvoiceDocument, pick: (line: InvoiceLine) => unknown): string | undefined => {
  const line = doc.lines.find((l) => pick(l) != null);
  return line ? 'ok' : undefined;
};

export const FIELD_DEFS: FieldDef[] = [
  // --- En-tête -----------------------------------------------------------
  { bt: 'BT-1', section: 'entete', minProfile: 0, optional: false, get: (d) => nonEmpty(d.number) },
  { bt: 'BT-2', section: 'entete', minProfile: 0, optional: false, get: (d) => nonEmpty(d.issueDate) },
  { bt: 'BT-3', section: 'entete', minProfile: 0, optional: false, get: (d) => nonEmpty(d.documentTypeCode) },
  { bt: 'BT-5', section: 'entete', minProfile: 0, optional: false, get: (d) => nonEmpty(d.currency) },
  { bt: 'BT-24', section: 'entete', minProfile: 0, optional: false, get: (d) => nonEmpty(d.profile.urn) },
  { bt: 'BT-23', section: 'entete', minProfile: 3, optional: true, get: (d) => nonEmpty(d.businessProcess) },
  { bt: 'BT-10', section: 'entete', minProfile: 3, optional: true, get: (d) => nonEmpty(d.buyerReference) },
  { bt: 'BT-13', section: 'entete', minProfile: 2, optional: true, get: (d) => nonEmpty(d.orderReference) },
  { bt: 'BT-14', section: 'entete', minProfile: 2, optional: true, get: (d) => nonEmpty(d.salesOrderReference) },
  { bt: 'BT-9', section: 'paiement', minProfile: 1, optional: true, get: (d) => nonEmpty(d.dueDate ?? d.payment.dueDate) },
  { bt: 'BT-72', section: 'livraison', minProfile: 3, optional: true, get: (d) => nonEmpty(d.deliveryDate) },

  // --- Vendeur -----------------------------------------------------------
  { bt: 'BT-27', section: 'vendeur', minProfile: 0, optional: false, get: (d) => nonEmpty(d.seller.name) },
  { bt: 'BT-28', section: 'vendeur', minProfile: 3, optional: true, get: (d) => nonEmpty(d.seller.tradingName) },
  { bt: 'BT-29', section: 'vendeur', minProfile: 3, optional: true, get: (d) => nonEmpty(d.seller.identifiers[0]?.value) },
  { bt: 'BT-30', section: 'vendeur', minProfile: 3, optional: true, get: (d) => nonEmpty(d.seller.legalRegistrationId) },
  { bt: 'BT-31', section: 'vendeur', minProfile: 1, optional: false, get: (d) => nonEmpty(d.seller.vatId) },
  { bt: 'BT-34', section: 'vendeur', minProfile: 1, optional: true, get: (d) => nonEmpty(d.seller.electronicAddress?.value) },
  { bt: 'BT-35', section: 'vendeur', minProfile: 1, optional: false, get: (d) => nonEmpty(d.seller.address?.line1) },
  { bt: 'BT-37', section: 'vendeur', minProfile: 1, optional: false, get: (d) => nonEmpty(d.seller.address?.city) },
  { bt: 'BT-38', section: 'vendeur', minProfile: 1, optional: false, get: (d) => nonEmpty(d.seller.address?.postcode) },
  { bt: 'BT-40', section: 'vendeur', minProfile: 1, optional: false, get: (d) => nonEmpty(d.seller.address?.country) },

  // --- Acheteur ----------------------------------------------------------
  { bt: 'BT-44', section: 'acheteur', minProfile: 0, optional: false, get: (d) => nonEmpty(d.buyer.name) },
  { bt: 'BT-48', section: 'acheteur', minProfile: 1, optional: true, get: (d) => nonEmpty(d.buyer.vatId) },
  { bt: 'BT-49', section: 'acheteur', minProfile: 2, optional: true, get: (d) => nonEmpty(d.buyer.electronicAddress?.value) },
  { bt: 'BT-50', section: 'acheteur', minProfile: 1, optional: false, get: (d) => nonEmpty(d.buyer.address?.line1) },
  { bt: 'BT-52', section: 'acheteur', minProfile: 1, optional: false, get: (d) => nonEmpty(d.buyer.address?.city) },
  { bt: 'BT-53', section: 'acheteur', minProfile: 1, optional: false, get: (d) => nonEmpty(d.buyer.address?.postcode) },
  { bt: 'BT-55', section: 'acheteur', minProfile: 1, optional: false, get: (d) => nonEmpty(d.buyer.address?.country) },

  // --- Lignes (BASIC et au-delà) ----------------------------------------
  { bt: 'BT-126', section: 'lignes', minProfile: 2, optional: true, get: (d) => allLines(d, (l) => l.id) },
  { bt: 'BT-153', section: 'lignes', minProfile: 2, optional: false, get: (d) => allLines(d, (l) => l.name) },
  { bt: 'BT-129', section: 'lignes', minProfile: 2, optional: false, get: (d) => allLines(d, (l) => l.quantity) },
  { bt: 'BT-130', section: 'lignes', minProfile: 2, optional: false, get: (d) => allLines(d, (l) => l.unitCode) },
  { bt: 'BT-146', section: 'lignes', minProfile: 2, optional: false, get: (d) => allLines(d, (l) => l.unitPrice) },
  { bt: 'BT-147', section: 'lignes', minProfile: 2, optional: true, get: (d) => someLine(d, (l) => l.itemDiscount) },
  { bt: 'BT-148', section: 'lignes', minProfile: 3, optional: true, syntaxes: ['CII'], get: (d) => someLine(d, (l) => l.grossPrice) },
  { bt: 'BT-131', section: 'lignes', minProfile: 2, optional: false, get: (d) => allLines(d, (l) => l.lineNetAmount) },
  { bt: 'BT-151', section: 'lignes', minProfile: 2, optional: false, get: (d) => allLines(d, (l) => l.vatCategory) },
  { bt: 'BT-152', section: 'lignes', minProfile: 2, optional: false, get: (d) => allLines(d, (l) => l.vatRate) },
  { bt: 'BT-155', section: 'lignes', minProfile: 2, optional: true, get: (d) => allLines(d, (l) => l.sellerItemId) },

  // --- TVA ---------------------------------------------------------------
  { bt: 'BT-118', section: 'tva', minProfile: 1, optional: false, get: (d) => (d.taxes.length && d.taxes.every((t) => t.category) ? 'ok' : undefined) },
  { bt: 'BT-119', section: 'tva', minProfile: 1, optional: false, get: (d) => (d.taxes.length && d.taxes.every((t) => t.rate != null) ? 'ok' : undefined) },
  { bt: 'BT-116', section: 'tva', minProfile: 1, optional: false, get: (d) => (d.taxes.length && d.taxes.every((t) => t.taxableAmount != null) ? 'ok' : undefined) },
  { bt: 'BT-117', section: 'tva', minProfile: 1, optional: false, get: (d) => (d.taxes.length && d.taxes.every((t) => t.taxAmount != null) ? 'ok' : undefined) },
  { bt: 'BT-120', section: 'tva', minProfile: 3, optional: true, get: (d) => (d.taxes.some((t) => t.exemptionReason) ? 'ok' : undefined) },

  // --- Totaux ------------------------------------------------------------
  { bt: 'BT-106', section: 'totaux', minProfile: 2, optional: false, get: (d) => nonEmpty(d.totals.lineNet?.toString()) },
  { bt: 'BT-107', section: 'totaux', minProfile: 1, optional: true, get: (d) => nonEmpty(d.totals.allowanceTotal?.toString()) },
  { bt: 'BT-108', section: 'totaux', minProfile: 1, optional: true, get: (d) => nonEmpty(d.totals.chargeTotal?.toString()) },
  { bt: 'BT-109', section: 'totaux', minProfile: 0, optional: false, get: (d) => nonEmpty(d.totals.taxBasis?.toString()) },
  { bt: 'BT-110', section: 'totaux', minProfile: 0, optional: false, get: (d) => nonEmpty(d.totals.taxTotal?.toString()) },
  { bt: 'BT-112', section: 'totaux', minProfile: 0, optional: false, get: (d) => nonEmpty(d.totals.grandTotal?.toString()) },
  { bt: 'BT-113', section: 'totaux', minProfile: 1, optional: true, get: (d) => nonEmpty(d.totals.prepaid?.toString()) },
  { bt: 'BT-115', section: 'totaux', minProfile: 0, optional: false, get: (d) => nonEmpty(d.totals.duePayable?.toString()) },

  // --- Paiement ----------------------------------------------------------
  { bt: 'BT-81', section: 'paiement', minProfile: 1, optional: false, get: (d) => nonEmpty(d.payment.meansCode) },
  { bt: 'BT-84', section: 'paiement', minProfile: 1, optional: true, get: (d) => nonEmpty(d.payment.iban) },
  { bt: 'BT-86', section: 'paiement', minProfile: 1, optional: true, get: (d) => nonEmpty(d.payment.bic) },
  { bt: 'BT-20', section: 'paiement', minProfile: 1, optional: true, get: (d) => nonEmpty(d.payment.terms) },
];

export interface FieldStat {
  bt: string;
  section: SectionId;
  optional: boolean;
  minProfile: number;
  applicable: number;
  hits: number;
  rate: number;
  /** Fichiers applicables où le champ manque. */
  misses: string[];
}

export interface HitRateReport {
  fixtureCount: number;
  fields: FieldStat[];
  byProfile: Record<string, number>;
  bySyntax: Record<string, number>;
}

export interface CorpusEntry {
  id: string;
  syntax: Syntax;
  profile: string;
  doc: InvoiceDocument;
}

/** Calcule la matrice pour un corpus de documents déjà extraits. */
export function computeHitRate(corpus: CorpusEntry[]): HitRateReport {
  const byProfile: Record<string, number> = {};
  const bySyntax: Record<string, number> = {};
  for (const entry of corpus) {
    byProfile[entry.profile] = (byProfile[entry.profile] ?? 0) + 1;
    bySyntax[entry.syntax] = (bySyntax[entry.syntax] ?? 0) + 1;
  }

  const fields: FieldStat[] = FIELD_DEFS.map((def) => {
    const applicableEntries = corpus.filter((entry) => {
      if (def.syntaxes && !def.syntaxes.includes(entry.syntax)) return false;
      return PROFILE_RANK[entry.profile as keyof typeof PROFILE_RANK] >= def.minProfile;
    });
    const misses: string[] = [];
    let hits = 0;
    for (const entry of applicableEntries) {
      if (def.get(entry.doc) !== undefined) hits += 1;
      else misses.push(entry.id);
    }
    return {
      bt: def.bt,
      section: def.section,
      optional: def.optional,
      minProfile: def.minProfile,
      applicable: applicableEntries.length,
      hits,
      rate: applicableEntries.length === 0 ? 0 : hits / applicableEntries.length,
      misses,
    };
  });

  // Rapport volontairement déterministe (pas d'horodatage) : régénérer la
  // matrice ne doit produire un diff que si les chiffres changent.
  return {
    fixtureCount: corpus.length,
    fields,
    byProfile,
    bySyntax,
  };
}
